import { FC, lazy, Suspense } from "react";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import { Logout, AuthPage, useAuth } from "../modules/auth";
import { App } from "../App";
import { MasterLayout } from "src/_metronic/layout/MasterLayout";

const Dashboard = lazy(() => import("../pages/dashboard/Dashboard"));
const Products = lazy(() => import("../pages/products/Products"));
const NewProduct = lazy(() => import("../pages/products/NewProduct"));
const ABTestStudio = lazy(() => import("../pages/products/ABTesStudio"));
const Listicles = lazy(() => import("../pages/listicle/Listicle"));
const NewListicle = lazy(() => import("../pages/listicle/NewListicle"));
const Templates = lazy(() => import("src/app/pages/templates/Templates"));
const UseTemplate = lazy(() => import("src/app/pages/templates/UseTemplate"));
const Pages = lazy(() => import("../pages/pages/Pages"));
const Campaigns = lazy(() => import("../pages/campaigns/Campaigns"));
const NewCampaign = lazy(() => import("../pages/campaigns/NewCampaign"));
const AiSortSnapshot = lazy(() => import("../pages/campaigns/aiSort/AiSortSnapshot"));
const Reports = lazy(() => import("../pages/reports/Reports"));
const Trends = lazy(() => import("../pages/trends/Trends"));
const TagManagement = lazy(() => import("../pages/tagManagement/TagManagement"));
const StrategyManagement = lazy(() => import("../pages/strategyManagement/StrategyManagement"));
const SpecialPageAdmin = lazy(() => import("../pages/special/SpecialPageAdmin.jsx"));
const SpecialPageHandler = lazy(() => import("../pages/special/SpecialPageHandler"));
const Error404 = lazy(() => import("../pages/Error404"));
const HealthCheck = lazy(() => import("../pages/HealthCheck"));

const { PUBLIC_URL } = process.env;

const SpecialPageDecision: FC<{ currentUser: any }> = ({ currentUser }) => {
  const knownRoutes = [
    "dashboard",
    "products",
    "listicles",
    "templates",
    "pages",
    "campaigns",
    "reports",
    "trends",
    "tagManagement",
    "strategyManagement",
    "special-pages",
    "auth",
    "logout",
    "error",
    "healthcheck",
  ];

  const pathname = window.location.pathname;
  const specialId = pathname.split("/").filter(Boolean).pop();

  if (specialId && knownRoutes.includes(specialId)) {
    return currentUser ? <Navigate to={`/${specialId}`} /> : <Navigate to="/auth" />;
  }

  const specialIdPattern = /^[a-zA-Z0-9-]{8,}$/;
  if (specialId && specialIdPattern.test(specialId)) {
    return <SpecialPageHandler />;
  }

  return <Error404 />;
};

const AppRoutes: FC = () => {
  const { currentUser } = useAuth();

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Suspense fallback={<div className="splash-screen">Loading...</div>}>
        <Routes>
          <Route element={<App />}>
            <Route path="healthcheck" element={<HealthCheck />} />
            <Route path="error/404" element={<Error404 />} />
            <Route path="logout" element={<Logout />} />
            <Route path="auth/*" element={<AuthPage />} />

            <Route
              index
              element={currentUser ? <Navigate to="/dashboard" /> : <Navigate to="/auth" />}
            />

            {currentUser ? (
              <Route element={<MasterLayout />}>
                <Route path="dashboard" element={<Dashboard />} />

                <Route path="products">
                  <Route index element={<Products />} />
                  <Route path="newProduct" element={<NewProduct />} />
                  <Route path="edit/:id" element={<NewProduct />} />
                  <Route path="ab-test-studio/:id" element={<ABTestStudio />} />
                </Route>

                <Route path="listicles">
                  <Route index element={<Listicles />} />
                  <Route path="newListicle" element={<NewListicle />} />
                  <Route path="edit/:id" element={<NewListicle />} />
                </Route>

                <Route path="templates">
                  <Route index element={<Templates />} />
                  <Route path="useTemplate" element={<UseTemplate />} />
                  <Route path="useTemplate/:id/:cardId" element={<UseTemplate />} />
                </Route>

                <Route path="pages" element={<Pages />} />
                <Route path="campaigns">
                  <Route index element={<Campaigns />} />
                  <Route path="newCampaign" element={<NewCampaign />} />
                  <Route path="ai-sort" element={<AiSortSnapshot />} />
                  <Route path=":campaignId/ai-sort" element={<AiSortSnapshot />} />
                </Route>

                <Route path="reports" element={<Reports />} />
                <Route path="trends" element={<Trends />} />
                <Route path="tagManagement" element={<TagManagement />} />
                <Route path="strategyManagement" element={<StrategyManagement />} />
                <Route path="special-pages" element={<SpecialPageAdmin />} />
              </Route>
            ) : (
              <Route path="dashboard/*" element={<Navigate to="/auth" />} />
            )}

            <Route path=":specialId" element={<SpecialPageDecision currentUser={currentUser} />} />

            <Route path="*" element={<Error404 />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export { AppRoutes };
